import i18next, { TFunction } from 'i18next';
import { isEqual, isEqualWith } from 'lodash';
import moment from 'moment';

import { AppConstants, ServiceConstants } from '../constants';
import { StorageUtils } from 'utils';
import { reset } from 'config/redux/slice';
import store from 'config/redux/index';
import { changeLanguageHeader } from 'config/axios';
import { CardResponse, CreditResponse, ICardRequestUpdate, ICreditRequestUpdate } from 'types';

const {
  CREDIT_CONSTANT_C_LOWER,
  CREDIT_CONSTANT_C_HIGHER,
  NATIONALITY_PRIORITY_LIST,
  STORAGE_ACCESS_TOKEN,
  STORAGE_VALID_TO,
} = AppConstants;

const {
  REQUEST_ERROR,
  REQUEST_PENDING,
  REQUEST_SUCCESS
} = ServiceConstants;

export const getCreditLimit = (creditAmount: number, paymentPeriod: number, interestRate: number): number => {
  if (!creditAmount || !paymentPeriod) {
    return 0;
  }
  const c = Math.pow(interestRate + 1, 1/12);
  const result = creditAmount * Math.pow(c, paymentPeriod) / (Math.pow(c, paymentPeriod!) - 1) * (c - 1);
  return Math.round(result);
}

export const populateMonthlyInstallment = (creditAmount: number, paymentPeriod: number, form: any) => {
  const minLimit = getCreditLimit(creditAmount, paymentPeriod, CREDIT_CONSTANT_C_LOWER);
  const maxLimit = getCreditLimit(creditAmount, paymentPeriod, CREDIT_CONSTANT_C_HIGHER);

  form.setFieldValue('monthlyInstallmentFrom', minLimit);
  form.setFieldValue('monthlyInstallmentTo', maxLimit);
}

export function resolveServiceObject(serviceObject: any, serviceId: string, onSuccess: (val: any) => void, onError?: (val: any) => void) {
  if (isEmpty(serviceObject)) {
    return;
  }
  if (serviceObject.status === REQUEST_PENDING) {
    return;
  }
  store.dispatch(reset(serviceId));
  const object = serviceObject;
  if (object.status === REQUEST_SUCCESS) {
    onSuccess(object.data);
  } else if (object.status === REQUEST_ERROR) {
    onError && onError(object);
  }
}

export function isEmpty(object: any) {
  if (object === undefined || object === null) {
    return true;
  }
  if (typeof object === 'number') {
    return false;
  } else if (typeof object === 'string') {
    return object.trim().length === 0;
  }
  if (object instanceof Array) {
    return object.length === 0;
  }
  if (object instanceof Date) {
    return !object;
  }
  if (typeof object === 'boolean') {
    return false;
  }
  return Object.keys(object).length === 0;
}

export function logout() {
  StorageUtils.deleteKey(STORAGE_ACCESS_TOKEN);
  StorageUtils.deleteKey(STORAGE_VALID_TO);
}

export function changeLanguage(lang: string) {
  changeLanguageHeader(lang.toLowerCase());
  i18next.changeLanguage(lang.toLowerCase());
}

export function validateField(
  fieldName: string,
  value: any,
  rules: {
    required?: boolean;
    min?: number;
    max?: number;
    requiredMessage?: string;
    minMessage?: string;
    maxMessage?: string;
  },
  form: any,
  t: TFunction,
) {
  const { required, min, max, requiredMessage, minMessage, maxMessage } = rules;

  if (required && (value === undefined || value === null || value === '')) {
    form.setFieldError(fieldName, requiredMessage ?? t('input.required'));
    return false;
  }
  if (typeof value === 'number') {
    if (min !== undefined && value < min) {
      form.setFieldError(fieldName, minMessage ?? t('input.min', { amount: min }));
      return false;
    }
    if (max !== undefined && value > max) {
      form.setFieldError(fieldName, maxMessage ?? t('input.max', { amount: max }));
      return false;
    }
  }
  return true;
};

const customizer = (objValue: any, othValue: any, key: any) => {
  if (key === 'id') return true;
  if (objValue === undefined && othValue === null) return true;
  if (objValue === null && othValue === undefined) return true;
  return undefined;
};

export function getChangedFieldsForCredit(values: CreditResponse, previousValues: CreditResponse): ICreditRequestUpdate[] {
  const changedFields: ICreditRequestUpdate[] = [];
  Object.keys(values).forEach((key) => {
    const typedKey = key as keyof CreditResponse;
    if (typedKey === 'monthlyInstallmentFrom' || typedKey === 'monthlyInstallmentTo') {
      return;
    }

    const currentValue = isEmpty(values[typedKey]) ? undefined : values[typedKey];
    const prevValue = isEmpty(previousValues[typedKey]) ? undefined : previousValues[typedKey];
    if (currentValue instanceof Date && prevValue instanceof Date) {
      const isSameDate = prevValue.getFullYear() === currentValue.getFullYear() && prevValue.getMonth() === currentValue.getMonth() && prevValue.getDate() === currentValue.getDate();
      if (!isSameDate) {
        changedFields.push({ name: typedKey, value: convertToAppropriateValue(values[typedKey]) });
      }
    } else if (!isEqualWith(currentValue, prevValue, customizer)) {
      changedFields.push({ name: typedKey, value: convertToAppropriateValue(values[typedKey]) });
    }
  });
  return changedFields;
}

export function getChangedFieldsForCard(values: CardResponse, previousValues: CardResponse): ICardRequestUpdate[] {
  const changedFields: ICardRequestUpdate[] = [];
  Object.keys(values).forEach((key) => {
    const typedKey = key as keyof CardResponse;
    if (typedKey === 'fullName' || typedKey === 'phoneNumber' || typedKey === 'birthDate' || typedKey === 'nationality' || typedKey === 'visaType') {
      return;
    }

    const currentValue = isEmpty(values[typedKey]) ? undefined : values[typedKey];
    const prevValue = isEmpty(previousValues[typedKey]) ? undefined : previousValues[typedKey];
    if (!isEqual(currentValue, prevValue)) {
      changedFields.push({ name: typedKey, value: convertToAppropriateValue(values[typedKey]) });
    }
  });
  return changedFields;
}

export function convertToAppropriateValue(value: any) {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  if (value === 'true' || value === 'false') {
    return value === 'true' ? true : false;
  }
  if (value instanceof Date) {
    return moment(value).format('YYYY-MM-DD');
  }
  if (value instanceof Array) {
    const newArray: any = new Array(value.length);
    value.forEach((el, index) => {
      newArray[index] = convertToAppropriateValue(el);
    })
    return newArray;
  }
  if (value instanceof Object) {
    const newObject: any = {};
    Object.keys(value).forEach(key => {
      newObject[key] = convertToAppropriateValue(value[key]);
    });
    return newObject;
  }
  return value;
}

export function addNewObject(form: any, object: any) {
  let tempArray = form.getTransformedValues();
  tempArray[Object.keys(tempArray).length] = object;
  form.setInitialValues(tempArray);
  form.reset();
}

export function removeObject(form: any, index: number, object: any) {
  let transformed: any = form.getTransformedValues();
  const tempArray: any[] = Object.keys(transformed)
    .map(key => transformed[key])
    .filter((_el, i) => i !== index);
  if (tempArray.length === 0 && object) {
    tempArray.push(object);
  }
  form.setInitialValues(tempArray);
  form.reset();
}

export function isYoungerThanTwelveMonths(date: Date): boolean {
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 12);

  return date > sixMonthsAgo;
}

export function getOrderedCountryList(nationalities: string[] | undefined, t: any) {
  if (!nationalities) {
    return [];
  }
  const top: string[] = [];
  const others: string[] = [];

  for (const item of nationalities) {
    if (item !== 'OTHER') {
      if (NATIONALITY_PRIORITY_LIST.includes(item)) {
        top.push(item);
      } else {
        others.push(item);
      }
    }
  }

  const topRenderedArray = top.map(el => ({ value: el, label: t(`input.nationality.options.${el}`)}));
  let otherRenderedArray = others.map(el => ({ value: el, label: t(`input.nationality.options.${el}`)}));
  otherRenderedArray.sort((a, b) => a.label.localeCompare(b.label));
  otherRenderedArray.push({ value: 'OTHER', label: t(`input.nationality.options.OTHER`) })

  return [
    ...topRenderedArray,
    ...otherRenderedArray
  ];
}